import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Skeleton } from './ui/skeleton';
import { Button } from './ui/button';
import {
  AlertOctagon, RefreshCw, ChevronRight, ChevronLeft, MessageCircle, Clock, X,
} from 'lucide-react';
import { cn } from '../lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_COLORS = {
  'Waiting for Metal': 'border-amber-300 text-amber-700 bg-amber-50 dark:bg-amber-500/10',
  'Waiting for Diamonds': 'border-purple-300 text-purple-700 bg-purple-50 dark:bg-purple-500/10',
  'Waiting for Chains': 'border-sky-300 text-sky-700 bg-sky-50 dark:bg-sky-500/10',
  'Delay Expected': 'border-rose-300 text-rose-700 bg-rose-50 dark:bg-rose-500/10',
  'Request New Direct Casting Piece(s)': 'border-indigo-300 text-indigo-700 bg-indigo-50 dark:bg-indigo-500/10',
  'Other': 'border-slate-300 text-slate-700 bg-slate-50 dark:bg-slate-500/10',
};

const formatAge = (hrs) => {
  if (hrs == null) return '';
  if (hrs < 1) return 'just now';
  if (hrs < 24) return `${Math.round(hrs)}h`;
  return `${Math.floor(hrs / 24)}d`;
};

const FactoryConcernsPanel = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ total_orders: 0, total_concerns: 0, by_type: {} });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [typeFilter, setTypeFilter] = useState('all');
  // Drawer open/close — persisted in localStorage so the user's preference sticks
  const [open, setOpen] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem('factoryConcernsPanelOpen') === '1';
  });
  const toggleOpen = () => {
    setOpen(o => {
      const next = !o;
      try { window.localStorage.setItem('factoryConcernsPanelOpen', next ? '1' : '0'); } catch (_e) { /* ignore quota */ }
      return next;
    });
  };

  const load = useCallback(async (silent = false) => {
    if (silent) setRefreshing(true); else setLoading(true);
    try {
      const res = await axios.get(`${API}/factory-concerns/active`);
      setItems(res.data?.items || []);
      setStats(res.data?.stats || { total_orders: 0, total_concerns: 0, by_type: {} });
    } catch (e) {
      setItems([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filteredItems = useMemo(() => {
    if (typeFilter === 'all') return items;
    return items
      .map(o => ({ ...o, concerns: o.concerns.filter(c => c.type === typeFilter) }))
      .filter(o => o.concerns.length > 0);
  }, [items, typeFilter]);

  const typeChips = Object.entries(stats.by_type || {})
    .sort((a, b) => b[1] - a[1]);

  return (
    <>
      {/* Floating tray-arrow trigger — always visible on the right edge.
          Shows a red badge with the open-concern count when there are any. */}
      <button
        type="button"
        onClick={toggleOpen}
        aria-label={open ? 'Close factory concerns panel' : `Open factory concerns panel (${stats.total_concerns} open)`}
        className={cn(
          'fixed right-0 top-1/2 -translate-y-1/2 z-40 flex items-center gap-1.5 px-2 py-3 rounded-l-md shadow-md transition-all',
          'bg-card hover:bg-muted border border-r-0 border-border',
          open && 'translate-x-[380px]'  // slide trigger off-screen when drawer is open (drawer has its own close button)
        )}
        data-testid="factory-concerns-tray-toggle"
      >
        <ChevronLeft className={cn('h-4 w-4 transition-transform', open && 'rotate-180')} />
        <div className="flex flex-col items-center leading-tight">
          <AlertOctagon className={cn('h-4 w-4', stats.total_concerns > 0 ? 'text-rose-500' : 'text-muted-foreground')} />
          <span className="text-[9px] mt-0.5 [writing-mode:vertical-rl] rotate-180 font-medium">Concerns</span>
        </div>
        {stats.total_concerns > 0 && (
          <span
            className="absolute -top-1.5 -left-1.5 min-w-[20px] h-5 px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center shadow"
            data-testid="factory-concerns-tray-badge"
          >
            {stats.total_concerns > 99 ? '99+' : stats.total_concerns}
          </span>
        )}
      </button>

      {/* Backdrop (mobile only — keeps page interactive on desktop) */}
      {open && (
        <div
          className="fixed inset-0 z-30 bg-black/30 md:hidden"
          onClick={toggleOpen}
          data-testid="factory-concerns-backdrop"
        />
      )}

      {/* Sliding drawer */}
      <aside
        className={cn(
          'fixed top-0 right-0 bottom-0 z-40 w-[92vw] sm:w-[380px] flex flex-col bg-background border-l shadow-2xl transition-transform duration-300 ease-out',
          open ? 'translate-x-0' : 'translate-x-full'
        )}
        data-testid="factory-concerns-panel"
        aria-hidden={!open}
      >
        <Card className="h-full flex flex-col rounded-none border-0 shadow-none">
          <CardHeader className="pb-3 flex-shrink-0">
            <div className="flex items-center justify-between gap-2">
              <CardTitle className="font-heading text-sm flex items-center gap-1.5">
                <AlertOctagon className="h-4 w-4 text-rose-500" />
                Factory Concerns
                {stats.total_concerns > 0 && (
                  <Badge className="bg-rose-500 text-white border-0 ml-1">{stats.total_concerns}</Badge>
                )}
              </CardTitle>
              <div className="flex items-center gap-1">
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-7 w-7"
                  onClick={() => load(true)}
                  disabled={refreshing}
                  data-testid="factory-concerns-refresh"
                  title="Refresh"
                >
                  <RefreshCw className={cn('h-3.5 w-3.5', refreshing && 'animate-spin')} />
                </Button>
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-7 w-7"
                  onClick={toggleOpen}
                  data-testid="factory-concerns-close"
                  title="Close panel"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <p className="text-[10px] text-muted-foreground -mt-0.5">
              {stats.total_orders} order{stats.total_orders === 1 ? '' : 's'} with open requests — oldest first
            </p>

            {/* Type filter chips */}
            {typeChips.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                <button
                  onClick={() => setTypeFilter('all')}
                  className={cn(
                    'text-[10px] px-1.5 py-0.5 rounded border transition-colors',
                    typeFilter === 'all' ? 'bg-foreground text-background border-foreground' : 'bg-background hover:bg-muted'
                  )}
                  data-testid="factory-concerns-filter-all"
                >
                  All · {stats.total_concerns}
                </button>
                {typeChips.map(([type, count]) => (
                  <button
                    key={type}
                    onClick={() => setTypeFilter(type === typeFilter ? 'all' : type)}
                    className={cn(
                      'text-[10px] px-1.5 py-0.5 rounded border transition-colors truncate max-w-[140px]',
                      typeFilter === type
                        ? TYPE_COLORS[type] || 'bg-foreground text-background border-foreground'
                        : 'bg-background hover:bg-muted'
                    )}
                    title={type}
                    data-testid={`factory-concerns-filter-${type.replace(/\s+/g, '-').toLowerCase()}`}
                  >
                    {type.length > 16 ? type.slice(0, 14) + '…' : type} · {count}
                  </button>
                ))}
              </div>
            )}
          </CardHeader>

          <CardContent className="pt-0 pb-3 flex-1 overflow-y-auto space-y-2.5">
            {loading ? (
              <>{[1, 2, 3].map(i => <Skeleton key={i} className="h-24 w-full" />)}</>
            ) : filteredItems.length === 0 ? (
              <div className="text-center py-10 text-xs text-muted-foreground">
                <AlertOctagon className="h-7 w-7 mx-auto mb-2 opacity-30" />
                <p>{stats.total_orders === 0 ? 'No open factory concerns.' : 'No concerns of this type.'}</p>
                <p className="text-[10px] mt-1 opacity-70">{stats.total_orders === 0 ? 'All factories are clear.' : 'Try a different filter.'}</p>
              </div>
            ) : (
              filteredItems.map(o => (
                <div
                  key={o.order_id}
                  className="border rounded-md p-2.5 hover:border-amber-300 hover:shadow-sm transition-all cursor-pointer bg-card"
                  onClick={() => navigate(`/orders/${o.order_id}`)}
                  data-testid={`factory-concern-order-${o.order_id}`}
                >
                  <div className="flex items-start justify-between gap-2 mb-1.5">
                    <div className="flex-1 min-w-0">
                      <p className="font-mono font-semibold text-xs truncate">{o.order_number}</p>
                      <p className="text-[11px] text-muted-foreground truncate">
                        {o.customer_name}{o.branch ? ` · ${o.branch}` : ''}
                      </p>
                    </div>
                    <div className="flex flex-col items-end gap-0.5 flex-shrink-0">
                      {o.priority && (
                        <Badge variant="outline" className="text-[9px] py-0 px-1">{o.priority}</Badge>
                      )}
                      {o.oldest_age_hours != null && (
                        <span className={cn(
                          'text-[9px] flex items-center gap-0.5 font-semibold',
                          o.oldest_age_hours > 48 ? 'text-rose-600' : o.oldest_age_hours > 24 ? 'text-amber-600' : 'text-slate-500'
                        )}>
                          <Clock className="h-2.5 w-2.5" /> {formatAge(o.oldest_age_hours)}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Factory + stage line */}
                  <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground mb-1.5">
                    {o.factory_name && <span className="truncate">@ {o.factory_name}</span>}
                    {o.current_stage && <span className="opacity-50">·</span>}
                    {o.current_stage && <span className="truncate">{o.current_stage}</span>}
                  </div>

                  {/* Concerns */}
                  <div className="space-y-1">
                    {o.concerns.map(c => (
                      <div
                        key={c.id}
                        className={cn(
                          'rounded border-l-2 px-2 py-1 text-[11px]',
                          (TYPE_COLORS[c.type] || TYPE_COLORS.Other).replace('border-', 'border-l-')
                        )}
                        data-testid={`factory-concern-${c.id}`}
                      >
                        <div className="flex items-center justify-between gap-1.5 mb-0.5">
                          <span className="font-semibold text-[10px] uppercase tracking-wider truncate">{c.type}</span>
                          <div className="flex items-center gap-1.5 flex-shrink-0 opacity-70">
                            {c.messages_count > 0 && (
                              <span className="flex items-center gap-0.5 text-[9px]">
                                <MessageCircle className="h-2.5 w-2.5" /> {c.messages_count}
                              </span>
                            )}
                            <span className="text-[9px]">{formatAge(c.age_hours)}</span>
                          </div>
                        </div>
                        {c.message && (
                          <p className="line-clamp-2 text-[11px] leading-snug">{c.message}</p>
                        )}
                        {c.delay_reason && c.type === 'Delay Expected' && (
                          <p className="text-[10px] italic opacity-80 mt-0.5">Reason: {c.delay_reason}</p>
                        )}
                        {(c.style_numbers || []).length > 0 && (
                          <p className="text-[10px] opacity-80 mt-0.5">
                            Styles: {c.style_numbers.slice(0, 4).join(', ')}{c.style_numbers.length > 4 ? `, +${c.style_numbers.length - 4}` : ''}
                          </p>
                        )}
                        {c.created_by && (
                          <p className="text-[9px] opacity-60 mt-0.5">— {c.created_by}</p>
                        )}
                      </div>
                    ))}
                  </div>

                  <div className="flex items-center justify-end mt-1.5 text-[10px] text-amber-700 dark:text-amber-400">
                    Open order <ChevronRight className="h-3 w-3" />
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>
      </aside>
    </>
  );
};

export default FactoryConcernsPanel;
